import type { GapAnalysis } from '@sdd-maturity/shared-types';
import type { DimensionName } from '@sdd-maturity/shared-types';
import { opportunityLabel } from '../lib/opportunity';

export interface DimensionHeatmapProps {
  gaps: GapAnalysis[];
}

const ALL_DIMENSIONS: DimensionName[] = [
  'constitution-maturity',
  'artifact-traceability',
  'supplemental-context',
  'feedback-loop',
  'ai-collaboration',
  'framework-detection',
];

// Opportunity ladder (de-redded): single-hue blue intensity, strongest = Big win.
function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'var(--genai-sem-info-strong)';
    case 'high':
      return 'var(--genai-sem-info)';
    case 'medium':
      // info (#3b82f6) at 55% — no token for this exact tint (see SDM follow-up).
      return 'rgba(59, 130, 246, 0.55)';
    case 'low':
      return 'var(--genai-sem-text-secondary)';
    default:
      return 'var(--genai-sem-text-muted)';
  }
}

/**
 * Colored heatmap grid of dimension severity across gap types (AC-11.2).
 * Rows = dimensions, columns = unique gap types.
 */
export function DimensionHeatmap({ gaps }: DimensionHeatmapProps) {
  if (gaps.length === 0) {
    return <p className="text-muted">No heatmap data available.</p>;
  }

  // Build a map: dimension -> gap severity
  const dimMap = new Map<DimensionName, { severity: string; frequency: number }>();
  for (const gap of gaps) {
    const existing = dimMap.get(gap.dimension);
    if (!existing || gap.frequency > existing.frequency) {
      dimMap.set(gap.dimension, {
        severity: gap.averageSeverity,
        frequency: gap.frequency,
      });
    }
  }

  return (
    <div className="card" data-testid="dimension-heatmap">
      <h3 className="text-lg font-semibold mb-md">Dimension Heatmap</h3>
      <div className="grid grid-cols-3 gap-sm">
        {ALL_DIMENSIONS.map((dim) => {
          const info = dimMap.get(dim);
          const color = info ? severityColor(info.severity) : 'var(--genai-sem-surface-raised)';
          return (
            <div
              key={dim}
              className="heatmap-cell"
              style={{
                backgroundColor: color,
                opacity: info ? 0.8 + (info.frequency / 100) * 0.2 : 0.3,
              }}
              title={info ? `${dim}: ${opportunityLabel(info.severity)} (freq: ${info.frequency.toString()})` : `${dim}: no data`}
            >
              {dim}
              {info && (
                <div style={{ fontSize: 'var(--genai-text-xs)', marginTop: 'var(--genai-space-xs)' }}>
                  {opportunityLabel(info.severity)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
